import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import classnames from 'classnames';
import styles from '../searchView.module.scss';
import Pagination from 'v2/common/Pagination/Pagination';
import { GenericObject, SearchEntity, SearchStateProps } from 'v2/models';
import {
  Columns,
  searchViewPath,
  TypeOfColumn,
} from 'v2/utils/searchViewUtils';
import useFetchSearchesPaginated from 'v2/hooks/useFetchSearchesPaginated';
import useGetPageNumber from 'v2/hooks/useGetPageNumber';
import SearchList from 'v2/common/SearchList/SearchList';
import SearchHeader from 'v2/common/SearchHeader/SearchHeader';
import { SearchItem } from 'v2/common/SearchList/SearchItem/SearchItem';
import SearchSpinner from 'v2/common/SearchViewComponents/SearchSpinner';
import NoSearchesMessageText from 'v2/common/SearchViewComponents/NoSearchesMessageText';
import FiltersRow from 'v2/common/SearchBar/FiltersRow';
import SearchBar from 'v2/common/SearchBar/SearchBar';
import useSelectConversationCount from 'v2/hooks/useSelectConversationCount';
import useSelectApprovedProposals from 'v2/hooks/useSelectApprovedProposals';
import useQueryParams from 'v2/hooks/useQueryParams';

const NoMatchingSearches = () => {
  return (
    <div
      className={classnames(
        styles.noSearchesTitle,
        'h-100 w-100 flex-column text-center mt-5'
      )}
    >
      <div>
        <h1>No matching requests</h1>
      </div>
    </div>
  );
};

const AllSearches = ({ user }: SearchStateProps) => {
  const history = useHistory();
  const location = useLocation();
  const { searchText } = useParams<{ searchText?: string }>();
  const queryParams = useQueryParams();
  const page = useGetPageNumber({
    onNotValidPageNumber: changePage,
  });
  const sortColumn = queryParams.get('sort') || '-last_internal_update';

  const {
    refetch,
    isFetching,
    data: { results, count } = {},
    isError,
    error,
  } = useFetchSearchesPaginated(
    {
      page,
      search_text: searchText || '',
      ordering: sortColumn,
    },
    ['allSearchesList']
  );

  useEffect(() => {
    refetch();
  }, [page, searchText, queryParams]);

  const pageTitle = 'All Requests';
  const columns: Columns = [
    {
      type: TypeOfColumn.title,
      label: 'Title',
    },
    {
      type: TypeOfColumn.originator,
      label: 'Originator',
      disabled: !!user.single_person_organization,
    },
    {
      type: TypeOfColumn.responses,
      label: 'Responses',
      responsesCount: useSelectApprovedProposals(),
    },
    {
      type: TypeOfColumn.conversations,
      label: 'Conversations',
      conversationCount: useSelectConversationCount(),
    },
    {
      type: TypeOfColumn.lastActivity,
      label: 'Last Activity',
      sortable: true,
      ascField: 'last_internal_update',
      descField: '-last_internal_update',
    },
  ];

  function changePage(page: number) {
    const searchParam = searchText ? `/${searchText}` : '';
    history.push({
      pathname: `${searchViewPath.allSearches}/${page}${searchParam}`,
      search: location.search,
    });
    window.scrollTo(0, 0);
  }

  const handleSearch = (text: string) => {
    history.push(`${searchViewPath.allSearches}/1/${text}`);
  };

  const handleSort = (sort: string) => {
    const params = new URLSearchParams({ sort });
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  if (isError) {
    if (error === 'Invalid page.') {
      changePage(1);
    }
    return <div data-testid="errorFetching"></div>;
  }

  const renderContent = () => {
    if (isFetching) {
      return <SearchSpinner />;
    } else if (!results || !results?.length) {
      return searchText ? (
        <NoMatchingSearches />
      ) : (
        <NoSearchesMessageText title="Your organization has not submitted a request yet!" />
      );
    } else {
      return (
        <>
          <SearchList
            className={classnames('all-searches', 'mt-3')}
            columns={columns}
            data={results}
            currentSortField={sortColumn}
            onSortChange={handleSort}
            renderSearchItem={(search: SearchEntity, columns: Columns) => {
              return (
                <SearchItem columns={columns} search={search} key={search.id} />
              );
            }}
          />
          <Pagination
            totalCountOfItems={count}
            callBack={(pageSelected) => {
              changePage(pageSelected);
            }}
            currentPageOfItems={page}
          />
        </>
      );
    }
  };

  return (
    <div className={classnames('fix-bg-images', styles.searchViewContainer)}>
      <span aria-level={1}>
        <SearchHeader pageTitle={pageTitle} />
      </span>
      <div className={classnames(styles.filterRow)}>
        <div className="container-xl">
          <div className={classnames(styles.filterContainer)}>
            <FiltersRow
              renderSearchBox={() => {
                return (
                  <div className="search-bar">
                    <SearchBar
                      placeHolder="Search"
                      currentText={searchText || ''}
                      onSearch={handleSearch}
                      lengthTextValidation={{
                        operator: '===',
                        number: 0,
                        debounce: 500,
                      }}
                    />
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
      <div className="container-xl">{renderContent()}</div>
    </div>
  );
};

const mapStateToProps = (state: GenericObject): SearchStateProps => {
  return {
    user: state.user.user,
    unreadMessages: state.chat.unreadMessages,
  };
};

export default connect<SearchStateProps>(mapStateToProps)(AllSearches);
